import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  COLORS,
  FONTS,
  MARGINS,
  TABLE_STYLES,
  getRiskLabel,
  getRiskColor,
  formatDate,
  formatDateTime,
} from "./pdfStyles";
import {
  renderChartToImage,
  createSleepTrendChartConfig,
  createSleepDurationChartConfig,
  createExerciseTrendChartConfig,
  createActivityTrendChartConfig,
} from "./renderChartToImage";

const addHeader = (doc, clinicianName) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header background
  doc.setFillColor(COLORS.primary.main);
  doc.rect(0, 0, pageWidth, 35, "F");

  // Title
  doc.setTextColor(COLORS.text.light);
  doc.setFontSize(FONTS.title);
  doc.setFont("helvetica", "bold");
  doc.text("ONCODIR", MARGINS.page, 15);

  // Subtitle
  doc.setFontSize(FONTS.subtitle);
  doc.setFont("helvetica", "normal");
  doc.text("Patient Report", MARGINS.page, 25);

  // Clinician name and date (right side)
  doc.setFontSize(FONTS.small);
  const reportDate = new Date().toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Generated: ${reportDate}`, pageWidth - MARGINS.page, 15, {
    align: "right",
  });
  if (clinicianName) {
    doc.text(`Clinician: ${clinicianName}`, pageWidth - MARGINS.page, 22, {
      align: "right",
    });
  }

  return 45; // Return Y position after header
};

const addFooter = (doc, pageNumber, totalPages) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(FONTS.small);
  doc.setTextColor(COLORS.text.secondary);
  doc.text(
    `Page ${pageNumber} of ${totalPages}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: "center" }
  );
  doc.text(
    `Generated by ONCODIR DEPO - ${formatDateTime(new Date())}`,
    pageWidth / 2,
    pageHeight - 5,
    { align: "center" }
  );
};

const addSection = (doc, title, yPos) => {
  doc.setFontSize(FONTS.heading);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.primary.main);
  doc.text(title, MARGINS.page, yPos);

  // Underline
  const titleWidth = doc.getTextWidth(title);
  doc.setDrawColor(COLORS.primary.main);
  doc.setLineWidth(0.5);
  doc.line(MARGINS.page, yPos + 2, MARGINS.page + titleWidth, yPos + 2);

  return yPos + MARGINS.section;
};

const addDemographics = (doc, userData, yPos) => {
  yPos = addSection(doc, "Demographics", yPos);

  const demographics = [
    ["Subject ID", userData.subject_id || "N/A"],
    ["Sex", userData.sex || "N/A"],
    ["Age", userData.age ? `${userData.age} years` : "N/A"],
    ["Height", userData.height ? `${userData.height} cm` : "N/A"],
    ["Weight", userData.weight ? `${userData.weight} kg` : "N/A"],
  ];

  // Calculate BMI if height and weight are available
  if (userData.height && userData.weight) {
    const heightInMeters = userData.height / 100;
    const bmi = (userData.weight / (heightInMeters * heightInMeters)).toFixed(1);
    demographics.push(["BMI", bmi]);
  }

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: demographics,
    theme: "plain",
    styles: {
      fontSize: FONTS.body,
      cellPadding: 3,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60 },
      1: { cellWidth: "auto" },
    },
    margin: { left: MARGINS.page, right: MARGINS.page },
  });

  return doc.lastAutoTable.finalY + MARGINS.section;
};

const addRiskAssessment = (doc, userData, yPos) => {
  yPos = addSection(doc, "Risk Assessment", yPos);

  const riskLevel = userData.latest_risk_level;
  const riskLabel = getRiskLabel(riskLevel);
  const riskColor = getRiskColor(riskLevel);

  doc.setFontSize(FONTS.body);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(COLORS.text.primary);

  if (riskLevel) {
    doc.text("Current Risk Level:", MARGINS.page, yPos);

    // Risk level badge
    doc.setFillColor(riskColor);
    doc.roundedRect(MARGINS.page + 60, yPos - 5, 40, 8, 2, 2, "F");
    doc.setTextColor(COLORS.text.light);
    doc.setFont("helvetica", "bold");
    doc.text(riskLabel, MARGINS.page + 80, yPos, { align: "center" });

    yPos += MARGINS.element;
    doc.setTextColor(COLORS.text.secondary);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FONTS.small);
    doc.text(
      `Last updated: ${formatDate(userData.risk_updated_at)}`,
      MARGINS.page,
      yPos
    );
  } else {
    doc.text("No risk assessment available", MARGINS.page, yPos);
  }

  return yPos + MARGINS.section;
};

const addRecommendations = (doc, recommendations, yPos) => {
  yPos = addSection(doc, "Recommendations", yPos);

  if (!recommendations || recommendations.length === 0) {
    doc.setFontSize(FONTS.body);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text.secondary);
    doc.text("No recommendations recorded", MARGINS.page, yPos);
    return yPos + MARGINS.section;
  }

  const tableData = recommendations.map((rec) => [
    rec.recommendation,
    rec.status?.replace("Status.", "") || "N/A",
    formatDate(rec.created_at),
    rec.notes || "-",
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Recommendation", "Status", "Date", "Notes"]],
    body: tableData,
    theme: "grid",
    headStyles: TABLE_STYLES.header,
    bodyStyles: TABLE_STYLES.body,
    alternateRowStyles: TABLE_STYLES.alternateRow,
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 25 },
      2: { cellWidth: 30 },
      3: { cellWidth: "auto" },
    },
    styles: {
      fontSize: FONTS.small,
      cellPadding: 3,
      overflow: "linebreak",
    },
    margin: { left: MARGINS.page, right: MARGINS.page, top: 45 },
    didDrawPage: (data) => {
      // Add header on new pages
      if (data.pageNumber > 1) {
        addHeader(doc, null);
      }
    },
  });

  return doc.lastAutoTable.finalY + MARGINS.section;
};

const addSleepChart = async (doc, sleepData, yPos, clinicianName) => {
  yPos = addSection(doc, "Sleep Trends", yPos);

  if (!sleepData || sleepData.length === 0) {
    doc.setFontSize(FONTS.body);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text.secondary);
    doc.text("No sleep data available", MARGINS.page, yPos);
    return yPos + MARGINS.section;
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const chartWidth = pageWidth - MARGINS.page * 2;
  const chartHeight = 70;

  try {
    // Generate sleep duration trend chart
    const durationChartConfig = createSleepDurationChartConfig(sleepData);
    const durationChartImage = await renderChartToImage(
      durationChartConfig,
      chartWidth * 2.5,
      chartHeight * 2.5
    );

    doc.addImage(
      durationChartImage,
      "PNG",
      MARGINS.page,
      yPos,
      chartWidth,
      chartHeight
    );
    yPos += chartHeight + MARGINS.element;

    // Generate sleep composition chart (stacked bar)
    const trendChartConfig = createSleepTrendChartConfig(sleepData);
    const trendChartImage = await renderChartToImage(
      trendChartConfig,
      chartWidth * 2.5,
      chartHeight * 2.5
    );

    // Check if we need a new page
    if (yPos + chartHeight > 270) {
      doc.addPage();
      yPos = addHeader(doc, clinicianName);
      yPos = addSection(doc, "Sleep Trends (continued)", yPos);
    }

    doc.addImage(
      trendChartImage,
      "PNG",
      MARGINS.page,
      yPos,
      chartWidth,
      chartHeight
    );
    yPos += chartHeight + MARGINS.section;

    // Add summary statistics
    const totalDays = sleepData.length;
    const avgDuration = sleepData.reduce((sum, d) => sum + (d.duration || 0), 0) / totalDays / 60;
    const avgDeep = sleepData.reduce((sum, d) => sum + (d.deep || 0), 0) / totalDays / 60;
    const avgRem = sleepData.reduce((sum, d) => sum + (d.rem || 0), 0) / totalDays / 60;

    doc.setFontSize(FONTS.small);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text.secondary);
    doc.text(
      `Based on ${totalDays} days of data | Avg Duration: ${avgDuration.toFixed(1)}h | Avg Deep: ${avgDeep.toFixed(1)}h | Avg REM: ${avgRem.toFixed(1)}h`,
      MARGINS.page,
      yPos
    );
    yPos += MARGINS.section;
  } catch (error) {
    console.error("Error generating sleep chart:", error);
    doc.setFontSize(FONTS.body);
    doc.setTextColor(COLORS.text.secondary);
    doc.text("Unable to generate sleep chart", MARGINS.page, yPos);
    yPos += MARGINS.section;
  }

  return yPos;
};

const addExerciseChart = async (doc, exerciseData, yPos, clinicianName) => {
  yPos = addSection(doc, "Exercise Trends", yPos);

  if (!exerciseData || exerciseData.length === 0) {
    doc.setFontSize(FONTS.body);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text.secondary);
    doc.text("No exercise data available", MARGINS.page, yPos);
    return yPos + MARGINS.section;
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const chartWidth = pageWidth - MARGINS.page * 2;
  const chartHeight = 70;

  try {
    // Generate activity trend line chart
    const activityChartConfig = createActivityTrendChartConfig(exerciseData);
    const activityChartImage = await renderChartToImage(
      activityChartConfig,
      chartWidth * 2.5,
      chartHeight * 2.5
    );

    doc.addImage(
      activityChartImage,
      "PNG",
      MARGINS.page,
      yPos,
      chartWidth,
      chartHeight
    );
    yPos += chartHeight + MARGINS.element;

    // Generate exercise composition chart (stacked bar)
    const exerciseChartConfig = createExerciseTrendChartConfig(exerciseData);
    const exerciseChartImage = await renderChartToImage(
      exerciseChartConfig,
      chartWidth * 2.5,
      chartHeight * 2.5
    );

    // Check if we need a new page
    if (yPos + chartHeight > 270) {
      doc.addPage();
      yPos = addHeader(doc, clinicianName);
      yPos = addSection(doc, "Exercise Trends (continued)", yPos);
    }

    doc.addImage(
      exerciseChartImage,
      "PNG",
      MARGINS.page,
      yPos,
      chartWidth,
      chartHeight
    );
    yPos += chartHeight + MARGINS.section;

    // Add summary statistics
    const totalDays = exerciseData.length;
    const avgLight = exerciseData.reduce((sum, d) => sum + (d.light || 0), 0) / totalDays;
    const avgModerate = exerciseData.reduce((sum, d) => sum + (d.moderate || 0), 0) / totalDays;
    const avgVigorous = exerciseData.reduce((sum, d) => sum + (d.vigorous || 0), 0) / totalDays;

    doc.setFontSize(FONTS.small);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text.secondary);
    doc.text(
      `Based on ${totalDays} days | Avg Light: ${avgLight.toFixed(0)}min | Avg Moderate: ${avgModerate.toFixed(0)}min | Avg Vigorous: ${avgVigorous.toFixed(0)}min`,
      MARGINS.page,
      yPos
    );
    yPos += MARGINS.section;
  } catch (error) {
    console.error("Error generating exercise chart:", error);
    doc.setFontSize(FONTS.body);
    doc.setTextColor(COLORS.text.secondary);
    doc.text("Unable to generate exercise chart", MARGINS.page, yPos);
    yPos += MARGINS.section;
  }

  return yPos;
};

export const generateUserReport = async (userData, clinicianName, sleepData = null, exerciseData = null) => {
  const doc = new jsPDF();
  let yPos;

  // Add header
  yPos = addHeader(doc, clinicianName);

  // Add demographics section
  yPos = addDemographics(doc, userData, yPos);

  // Add risk assessment section
  yPos = addRiskAssessment(doc, userData, yPos);

  // Check if we need a new page before recommendations
  if (yPos > 200) {
    doc.addPage();
    yPos = addHeader(doc, clinicianName);
  }

  // Add recommendations section
  yPos = addRecommendations(doc, userData.recommendations, yPos);

  // Add sleep chart section if data is available
  if (sleepData && sleepData.length > 0) {
    // Check if we need a new page before sleep charts
    if (yPos > 150) {
      doc.addPage();
      yPos = addHeader(doc, clinicianName);
    }
    yPos = await addSleepChart(doc, sleepData, yPos, clinicianName);
  }

  // Add exercise chart section if data is available
  if (exerciseData && exerciseData.length > 0) {
    // Check if we need a new page before exercise charts
    if (yPos > 150) {
      doc.addPage();
      yPos = addHeader(doc, clinicianName);
    }
    yPos = await addExerciseChart(doc, exerciseData, yPos, clinicianName);
  }

  // Add footers to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages);
  }

  // Generate filename
  const subjectId = userData.subject_id || "unknown";
  const dateStr = new Date().toISOString().split("T")[0];
  const filename = `oncodir_report_${subjectId}_${dateStr}.pdf`;

  // Save the PDF
  doc.save(filename);

  return filename;
};

export default generateUserReport;
